import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authenticate, ROLE_LABELS, ROLE_COLORS } from "../auth";
import { useTheme } from "../DataContext";
import { buildGlobalStyles, getThemeNames } from "../theme";


export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [showPass, setShowPass]  = useState(false);
  const { theme, themeName, setTheme } = useTheme();
  const navigate = useNavigate();

  // Apply global styles
  useEffect(() => {
    let el = document.getElementById("pmo-global-styles");
    if (!el) { el = document.createElement("style"); el.id = "pmo-global-styles"; document.head.appendChild(el); }
    el.textContent = buildGlobalStyles(theme);
  }, [theme]);

  // Redirect if already logged in
  useEffect(() => {
    if (sessionStorage.getItem("pmo_user")) navigate("/portfolio");
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    await new Promise(r => setTimeout(r, 600)); // Auth delay simulation
    const user = authenticate(username.trim(), password);
    if (user) {
      sessionStorage.setItem("pmo_user", JSON.stringify({
        ...user, loginAt: new Date().toISOString(),
      }));
      navigate("/portfolio");
    } else {
      setError("ACCESS DENIED — INVALID CREDENTIALS");
    }
    setLoading(false);
  };

  // Demo credentials list
  const DEMO_CREDS = [
    { role: "pmodirector", user: "hassaan",    pass: "ShurikenPMO#Hassaan2026!" },
    { role: "board",       user: "board",      pass: "Board@PMO2026!" },
    { role: "clevel",      user: "ceo",        pass: "CLevel@PMO2026!" },
    { role: "analyst",     user: "analyst",    pass: "Analyst@PMO2026!" },
    { role: "consultant",  user: "consultant", pass: "Consult@PMO2026!" },
  ];

  return (
    <div style={{
      minHeight: "100vh", background: theme.bg,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      position: "relative", overflow: "hidden",
    }}>
      {/* Animated scanline */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: "2px",
        background: `linear-gradient(90deg, transparent, ${theme.accent}88, transparent)`,
        animation: "scanline 4s linear infinite",
        pointerEvents: "none",
      }} />

      {/* Background grid pattern */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: `
          linear-gradient(${theme.cardBorder}22 1px, transparent 1px),
          linear-gradient(90deg, ${theme.cardBorder}22 1px, transparent 1px)
        `,
        backgroundSize: "40px 40px",
        pointerEvents: "none",
      }} />

      {/* Theme picker */}
      <div style={{ position: "absolute", top: "20px", right: "20px", zIndex: 100 }}>
        <select
          value={themeName}
          onChange={e => { setTheme(e.target.value); window.location.reload(); }}
          style={{
            width: "148px", padding: "7px 10px",
            background: theme.card, border: `1px solid ${theme.cardBorder}`,
            borderRadius: "8px", color: theme.textSecondary,
            fontFamily: "'Share Tech Mono', monospace",
            fontSize: "10px", letterSpacing: "1px",
            cursor: "pointer", outline: "none",
          }}
        >
          {getThemeNames().map(t => (
            <option key={t.key} value={t.key}>{t.name}</option>
          ))}
        </select>

      </div>

      {/* Login Card */}
      <div style={{
        width: "420px", maxWidth: "calc(100vw - 32px)",
        background: theme.card,
        border: `1px solid ${theme.accent}44`,
        borderRadius: "16px",
        padding: "36px 32px",
        boxShadow: `0 0 40px ${theme.accentGlow}, 0 20px 60px rgba(0,0,0,0.5)`,
        position: "relative", zIndex: 10,
        animation: "fadeInUp 0.4s ease forwards",
      }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: "28px" }}>
          <div style={{
            fontSize: "48px", color: theme.accent,
            filter: `drop-shadow(0 0 16px ${theme.accent}88)`,
            marginBottom: "8px",
            display: "inline-block",
            animation: "pulse-glow 3s ease-in-out infinite",
          }}>✦</div>
          <div style={{
            fontFamily: "'Orbitron', monospace",
            fontSize: "22px", fontWeight: 900,
            color: theme.accent,
            letterSpacing: "4px",
            filter: `drop-shadow(0 0 12px ${theme.accent}66)`,
          }}>SHURIKEN</div>
          <div style={{
            fontFamily: "'Orbitron', monospace",
            fontSize: "11px", color: theme.textMuted,
            letterSpacing: "3px", marginTop: "4px",
          }}>PMO INTELLIGENT PLATFORM</div>
          <div style={{
            fontFamily: "'Share Tech Mono', monospace",
            fontSize: "10px", color: theme.textMuted,
            letterSpacing: "1px", marginTop: "6px",
            opacity: 0.7,
          }}>PMO-P v1.0 · Shuriken Technologies</div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "16px" }}>
            <label style={{
              display: "block", fontSize: "10px",
              color: theme.textMuted, letterSpacing: "1px",
              textTransform: "uppercase", marginBottom: "6px",
              fontWeight: 600,
            }}>Username</label>
            <input
              type="text"
              value={username}
              onChange={e => { setUsername(e.target.value); setError(""); }}
              placeholder="Enter username"
              autoComplete="username"
              style={{ width: "100%", background: theme.bg }}
              required
            />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{
              display: "block", fontSize: "10px",
              color: theme.textMuted, letterSpacing: "1px",
              textTransform: "uppercase", marginBottom: "6px",
              fontWeight: 600,
            }}>Password</label>
            <div style={{ position: "relative" }}>
              <input
                type={showPass ? "text" : "password"}
                value={password}
                onChange={e => { setPassword(e.target.value); setError(""); }}
                placeholder="Enter password"
                autoComplete="current-password"
                style={{ width: "100%", background: theme.bg, paddingRight: "40px" }}
                required
              />
              <button
                type="button"
                onClick={() => setShowPass(!showPass)}
                style={{
                  position: "absolute", right: "10px", top: "50%",
                  transform: "translateY(-50%)",
                  background: "none", border: "none",
                  color: theme.textMuted, cursor: "pointer", fontSize: "14px",
                }}
              >{showPass ? "◑" : "◎"}</button>
            </div>
          </div>

          {error && (
            <div style={{
              padding: "10px 14px",
              background: "rgba(255,61,87,0.12)",
              border: "1px solid rgba(255,61,87,0.3)",
              borderRadius: "6px", marginBottom: "16px",
              color: "#FF3D57",
              fontSize: "12px", fontWeight: 600, letterSpacing: "0.5px",
              textAlign: "center",
            }}>{error}</div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-primary"
            style={{ width: "100%", padding: "12px", fontSize: "14px", letterSpacing: "2px" }}
          >
            {loading ? "AUTHENTICATING..." : "ACCESS SYSTEM"}
          </button>
        </form>

        {/* Divider */}
        <div style={{
          display: "flex", alignItems: "center", gap: "12px",
          margin: "20px 0",
        }}>
          <div style={{ flex: 1, height: "1px", background: theme.cardBorder }} />
          <span style={{ fontSize: "10px", color: theme.textMuted, letterSpacing: "1px" }}>
            DEMO ACCESS
          </span>
          <div style={{ flex: 1, height: "1px", background: theme.cardBorder }} />
        </div>

        {/* Demo credentials */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {DEMO_CREDS.map(c => (
            <button
              key={c.user}
              onClick={() => { setUsername(c.user); setPassword(c.pass); setError(""); }}
              style={{
                padding: "4px 10px", borderRadius: "14px",
                background: `${ROLE_COLORS[c.role]}18`,
                border: `1px solid ${ROLE_COLORS[c.role]}44`,
                color: ROLE_COLORS[c.role],
                fontSize: "10px", fontWeight: 600,
                cursor: "pointer",
                letterSpacing: "0.3px",
              }}
            >{ROLE_LABELS[c.role]}</button>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div style={{
        position: "absolute", bottom: "16px",
        fontSize: "10px", color: theme.textMuted,
        fontFamily: "'Share Tech Mono', monospace",
        letterSpacing: "0.5px",
      }}>
        © 2026 Cyber Shuriken Technologies · cybershuriken.com · Unauthorized access prohibited
      </div>
    </div>
  );
}
